import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Alert,
  Spinner,
  Table,
  Badge,
  Pagination,
  PaginationItem,
  PaginationLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button
} from 'reactstrap';
import Breadcrumbs from '../../../components/Common/Breadcrumb';
import { fetchRankingNasional, fetchRankingMe } from '../../../services/rankingNasionalService';

const NationalRankPage = () => {
  document.title = "Ranking Nasional";

  const location = useLocation();

  const [rankingNasional, setRankingNasional] = useState([]);
  const [rankingMe, setRankingMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, page: 1, per_page: 10, last_page: 1 });

  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [modalErrorMessage, setModalErrorMessage] = useState('');
  
  // NOTE: showSubscriptionButton state is no longer needed since we are not showing the button.

  const toggleErrorModal = () => setIsErrorModalOpen(!isErrorModalOpen);

  const { idTipeKonten, idJenisSeleksi } = useMemo(() => {
    const queryParams = new URLSearchParams(location.search);
    return {
      idTipeKonten: queryParams.get('id_tipe_konten'),
      idJenisSeleksi: queryParams.get('id_jenis_seleksi'),
    };
  }, [location.search]);

  const fetchRankings = useCallback(async (page = 1) => {
    if (!idTipeKonten || !idJenisSeleksi) {
      setError("Parameter URL tidak lengkap: id_tipe_konten dan id_jenis_seleksi diperlukan.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const [nasionalResponse, meResponse] = await Promise.all([
        fetchRankingNasional(idTipeKonten, idJenisSeleksi, page),
        fetchRankingMe(idTipeKonten, idJenisSeleksi)
      ]);

      if (nasionalResponse.success && Array.isArray(nasionalResponse.data)) {
        setRankingNasional(nasionalResponse.data);
        if (nasionalResponse.meta) {
          setPaginationMeta({
            page: nasionalResponse.meta.page || 1,
            per_page: nasionalResponse.meta.per_page || 10,
            total: nasionalResponse.meta.total || 0,
            last_page: nasionalResponse.meta.last_page || Math.ceil((nasionalResponse.meta.total || 0) / (nasionalResponse.meta.per_page || 10))
          });
        }
      } else {
        setRankingNasional([]);
        setPaginationMeta({ total: 0, page: 1, per_page: 10, last_page: 1 });
      }

      if (meResponse.success && meResponse.data) {
        setRankingMe(meResponse.data);
      } else {
        setRankingMe(null);
      }
    } catch (err) {
      console.error("Error fetching rankings:", err);
      
      const errorMessage = err.response?.data?.message || err.message || "Terjadi kesalahan saat mengambil data ranking. Mohon coba lagi.";
      
      if (errorMessage.includes("anda belum berlangganan")) {
        // PERUBAHAN DI SINI: Sesuaikan pesan modal
        setModalErrorMessage("Untuk mengakses paket premium ini, Anda harus memiliki langganan aktif.");
        setIsErrorModalOpen(true);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }, [idTipeKonten, idJenisSeleksi]);

  useEffect(() => {
    fetchRankings();
  }, [fetchRankings]);

  // Helper Functions
  const getScoreBadgeColor = useCallback((score) => {
    if (score >= 400) return 'bg-success-subtle text-success';
    if (score >= 300) return 'bg-warning-subtle text-warning';
    return 'bg-danger-subtle text-danger';
  }, []);

  const getRankBadgeColor = useCallback((rank) => {
    if (rank === 1) return 'bg-warning text-dark';
    if (rank === 2) return 'bg-secondary text-white';
    if (rank === 3) return 'bg-danger text-white';
    return 'bg-primary-subtle text-primary';
  }, []);

  // Custom Pagination Component
  const CustomPagination = () => {
    const { page, last_page, total, per_page } = paginationMeta;
    if (total === 0) return null;

    const getPageNumbers = () => {
      const pages = [];
      const maxVisiblePages = 5;
      let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
      let endPage = Math.min(last_page, startPage + maxVisiblePages - 1);

      if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        pages.push(
          <PaginationItem key={i} active={i === page}>
            <PaginationLink onClick={() => fetchRankings(i)}>
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
      return pages;
    };

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Menampilkan <span className="fw-semibold">{((page - 1) * per_page) + 1}</span> sampai{' '}
          <span className="fw-semibold">{Math.min(page * per_page, total)}</span> dari{' '}
          <span className="fw-semibold">{total}</span> data
        </div>
        <div className="d-flex align-items-center">
          <Pagination className="pagination pagination-rounded mb-0">
            <PaginationItem disabled={page === 1}>
              <PaginationLink
                previous
                onClick={() => page > 1 && fetchRankings(page - 1)}
              >
                &lt;
              </PaginationLink>
            </PaginationItem>
            {getPageNumbers()}
            <PaginationItem disabled={page === last_page}>
              <PaginationLink
                next
                onClick={() => page < last_page && fetchRankings(page + 1)}
              >
                &gt;
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page-content d-flex flex-column justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
        <Spinner color="primary" className="mb-2" />
        <p className="text-muted">Memuat data ranking...</p>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content bg-light-gray">
        <Container fluid>
          <Breadcrumbs title="Ranking" breadcrumbItem="Ranking Nasional" />
          {error && (
            <Row>
              <Col lg={12}>
                <Alert color="danger" className="text-center mt-3 shadow-sm rounded-3">
                  <i className="mdi mdi-alert-circle-outline me-2"></i> {error}
                </Alert>
              </Col>
            </Row>
          )}

          <Row className="mt-4 g-4">
            {rankingMe ? (
              <>
                <Col md={4}>
                  <Card className="card-custom-elevated h-100">
                    <CardBody className="p-4 d-flex flex-column align-items-center justify-content-center text-center">
                      <div className="avatar-md mx-auto mb-2 bg-warning-subtle text-warning rounded-circle d-flex align-items-center justify-content-center">
                        <i className="mdi mdi-trophy-award font-size-28"></i>
                      </div>
                      <h6 className="text-muted mb-1">Peringkat Anda</h6>
                      <h2 className="mb-0 fw-bold text-dark font-size-28">#{rankingMe.ranking}</h2>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="card-custom-elevated h-100">
                    <CardBody className="p-4 d-flex flex-column align-items-center justify-content-center text-center">
                      <div className="avatar-md mx-auto mb-2 bg-success-subtle text-success rounded-circle d-flex align-items-center justify-content-center">
                        <i className="mdi mdi-file-document-multiple-outline font-size-28"></i>
                      </div>
                      <h6 className="text-muted mb-1">Total Skor</h6>
                      <h2 className="mb-0 fw-bold text-dark font-size-28">{rankingMe.total_skor}</h2>
                    </CardBody>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card className="card-custom-elevated h-100">
                    <CardBody className="p-4 d-flex flex-column align-items-center justify-content-center text-center">
                      <div className="avatar-md mx-auto mb-2 bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center">
                        <i className="mdi mdi-account-group font-size-28"></i>
                      </div>
                      <h6 className="text-muted mb-1">Total Peserta</h6>
                      <h2 className="mb-0 fw-bold text-dark font-size-28">{rankingMe.total_peserta}</h2>
                    </CardBody>
                  </Card>
                </Col>
              </>
            ) : (
              <Col lg={12}>
                <Alert color="info" className="border-0 text-center font-size-14 py-3 rounded" role="alert">
                  <i className="mdi mdi-information-outline me-1"></i> Anda belum memiliki ranking.
                </Alert>
              </Col>
            )}

            <Col lg={12}>
              <Card className="card-custom shadow-lg h-100">
                <CardBody className="p-4">
                  <h5 className="card-title text-dark fw-bold mb-3">
                    <i className="mdi mdi-chart-line me-2 text-primary"></i> Ranking Nasional
                  </h5>
                  {rankingNasional.length > 0 ? (
                    <div className="table-responsive">
                      <Table className="table mb-0 table-hover table-striped">
                        <thead className="table-light">
                          <tr>
                            <th className="text-center">#</th>
                            <th>Nama</th>
                            <th>Provinsi</th>
                            <th className="text-center">Skor Total</th>
                            <th className="text-center d-none d-md-table-cell">TWK</th>
                            <th className="text-center d-none d-md-table-cell">TIU</th>
                            <th className="text-center d-none d-md-table-cell">TKP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rankingNasional.map((rankItem) => (
                            <tr key={rankItem.ranking}>
                              <td className="text-center fw-bold">
                                <Badge className={`font-size-14 fw-bold px-2 py-1 ${getRankBadgeColor(rankItem.ranking)}`}>
                                  {rankItem.ranking}
                                </Badge>
                              </td>
                              <td>{rankItem.full_name || (rankItem.username && rankItem.username.split('@')[0]) || '-'}</td>
                              <td>{rankItem.provinsi || '-'}</td>
                              <td className="text-center">
                                <Badge className={`font-size-14 fw-bold px-2 py-1 ${getScoreBadgeColor(rankItem.total)}`}>
                                  {rankItem.total}
                                </Badge>
                              </td>
                              <td className="text-center d-none d-md-table-cell">{rankItem.twk}</td>
                              <td className="text-center d-none d-md-table-cell">{rankItem.tiu}</td>
                              <td className="text-center d-none d-md-table-cell">{rankItem.tkp}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      <CustomPagination />
                    </div>
                  ) : (
                    <div className="alert alert-info border-0 text-center font-size-14 py-3 rounded" role="alert">
                      <i className="mdi mdi-information-outline me-1"></i> Belum ada data ranking nasional.
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      
      {/* PERUBAHAN DI SINI: Komponen Modal agar sesuai dengan skrinsut */}
      <Modal isOpen={isErrorModalOpen} toggle={toggleErrorModal} centered>
        <ModalHeader toggle={toggleErrorModal}>Info</ModalHeader>
        <ModalBody className="text-center py-4">
          <div className="mb-3">
            <i className="mdi mdi-alert-circle-outline font-size-40 text-warning"></i>
          </div>
          <h5 className="mb-2">anda belum berlangganan</h5>
          <p className="text-muted">{modalErrorMessage}</p>
        </ModalBody>
        <ModalFooter className="justify-content-center border-0 pt-0">
          <Button color="secondary" onClick={toggleErrorModal} className="w-100">
            Tutup
          </Button>
        </ModalFooter>
      </Modal>

      <style>
        {`
        .bg-light-gray { background-color: #f8f9fa; }
        .page-content { padding-top: 90px; padding-bottom: 60px; }
        .card-custom {
            border-radius: 12px;
            overflow: hidden;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .card-custom-elevated {
            border-radius: 12px;
            box-shadow: 0 5px 15px rgba(0,0,0,0.08);
            transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .card-custom-elevated:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.1);
        }
        .avatar-md { width: 48px; height: 48px; font-size: 24px; }
        .font-size-12 { font-size: 0.75rem !important; }
        .font-size-14 { font-size: 0.875rem !important; }
        .font-size-24 { font-size: 1.5rem !important; }
        .font-size-28 { font-size: 1.75rem !important; }
        .font-size-40 { font-size: 2.5rem !important; }
        .bg-success-subtle { background-color: #d4edda !important; }
        .text-success { color: #155724 !important; }
        .bg-warning-subtle { background-color: #fff3cd !important; }
        .text-warning { color: #856404 !important; }
        .bg-danger-subtle { background-color: #f8d7da !important; }
        .text-danger { color: #721c24 !important; }
        .bg-primary-subtle { background-color: #eaf4ff !important; }
        .text-primary { color: #007bff !important; }
        .bg-info-subtle { background-color: #d6e8f8 !important; }
        .text-info { color: #0c5460 !important; }
        `}
      </style>
    </React.Fragment>
  );
};

export default NationalRankPage;